import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../../utils/api';
import { MapContainer, TileLayer, Marker, Circle, useMapEvents, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { MapPin, Trash2, Save, Target } from 'lucide-react';

const crosshairIcon = new L.DivIcon({
  className: 'geofence-center',
  html: `<div style="width:20px;height:20px;border-radius:50%;background:#E8613A;border:3px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,0.4)"></div>`,
  iconSize: [20, 20],
  iconAnchor: [10, 10],
});

function MapClickHandler({ onMapClick }) {
  useMapEvents({
    click(e) {
      onMapClick(e.latlng);
    },
  });
  return null;
}

function FlyToCenter({ center }) {
  const map = useMap();
  useEffect(() => {
    if (center) {
      map.flyTo(center, 15, { duration: 0.8 });
    }
  }, [center, map]);
  return null;
}

export default function GeofenceTab({ projectId, project, onSaved }) {
  const [geofence, setGeofence] = useState(null);
  const [center, setCenter] = useState(null);
  const [radius, setRadius] = useState(200);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (project?.geofence) {
      const gf = project.geofence;
      setGeofence(gf);
      setCenter([gf.latitude, gf.longitude]);
      setRadius(gf.radius_meters || 200);
    }
  }, [project]);

  const handleMapClick = useCallback((latlng) => {
    setCenter([latlng.lat, latlng.lng]);
    setHasChanges(true);
  }, []);

  const handleSave = async () => {
    if (!center) {
      toast.error('Klicka på kartan för att placera geofence-mittpunkt');
      return;
    }
    setSaving(true);
    try {
      await api.setProjectGeofence(projectId, {
        latitude: center[0],
        longitude: center[1],
        radius_meters: radius,
      });
      toast.success('Geofence sparad!');
      setGeofence({ latitude: center[0], longitude: center[1], radius_meters: radius });
      setHasChanges(false);
      onSaved?.();  // Notify parent (t.ex. ProjectDetail) att uppdatera projekt-datan → wizard flyttar sig fram
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunde inte spara geofence');
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async () => {
    try {
      await api.removeProjectGeofence(projectId);
      toast.success('Geofence borttagen');
      setGeofence(null);
      setCenter(null);
      setHasChanges(false);
    } catch (err) {
      toast.error('Kunde inte ta bort geofence');
    }
  };

  const defaultCenter = [59.3293, 18.0686]; // Stockholm

  return (
    <div className="space-y-4" data-testid="geofence-tab">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div>
          <h3 className="text-base font-bold tracking-tight flex items-center gap-2">
            <Target size={18} className="text-[#E8613A]" />
            Geofence — Projektområde
          </h3>
          <p className="text-sm text-muted-foreground mt-0.5">
            Klicka på kartan för att placera mittpunkt. Arbetare som stämplar in utanför zonen får en varning.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {geofence && (
            <Button variant="outline" size="sm" onClick={handleRemove} data-testid="remove-geofence-btn">
              <Trash2 size={14} className="mr-1.5" /> Ta bort
            </Button>
          )}
          <Button
            size="sm"
            onClick={handleSave}
            disabled={saving || !center}
            className="bg-[#E8613A] hover:bg-[#d4552f] text-white"
            data-testid="save-geofence-btn"
          >
            <Save size={14} className="mr-1.5" />
            {saving ? 'Sparar...' : 'Spara geofence'}
          </Button>
        </div>
      </div>

      {/* Radius control */}
      <div className="flex items-center gap-4 bg-card border border-border rounded-lg p-3">
        <div className="flex items-center gap-2">
          <Label className="text-sm whitespace-nowrap">Radie:</Label>
          <Input
            type="number"
            min={50}
            max={5000}
            step={50}
            value={radius}
            onChange={(e) => { setRadius(parseInt(e.target.value) || 200); setHasChanges(true); }}
            className="w-24 h-8 text-sm"
            data-testid="geofence-radius-input"
          />
          <span className="text-sm text-muted-foreground">meter</span>
        </div>
        <input
          type="range"
          min={50}
          max={2000}
          step={50}
          value={radius}
          onChange={(e) => { setRadius(parseInt(e.target.value)); setHasChanges(true); }}
          className="flex-1 h-2 bg-muted rounded-full appearance-none cursor-pointer accent-[#E8613A]"
          data-testid="geofence-radius-slider"
        />
        {center && (
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <MapPin size={12} />
            {center[0].toFixed(4)}, {center[1].toFixed(4)}
          </div>
        )}
      </div>

      {/* Map */}
      <div className="bg-card border border-border rounded-xl overflow-hidden shadow-sm" style={{ height: 400 }}>
        <MapContainer
          center={center || defaultCenter}
          zoom={center ? 15 : 7}
          style={{ height: '100%', width: '100%' }}
          data-testid="geofence-map"
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <MapClickHandler onMapClick={handleMapClick} />
          {center && <FlyToCenter center={center} />}
          {center && (
            <>
              <Marker position={center} icon={crosshairIcon} />
              <Circle
                center={center}
                radius={radius}
                pathOptions={{
                  color: '#E8613A',
                  fillColor: '#E8613A',
                  fillOpacity: 0.12,
                  weight: 2,
                  dashArray: '6 4',
                }}
              />
            </>
          )}
        </MapContainer>
      </div>

      {/* Status */}
      {geofence && !hasChanges && (
        <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 rounded-lg p-3" data-testid="geofence-active-status">
          <Target size={16} />
          <span>Geofence aktiv: radie {geofence.radius_meters}m runt ({geofence.latitude.toFixed(4)}, {geofence.longitude.toFixed(4)})</span>
        </div>
      )}
      {hasChanges && center && (
        <div className="flex items-center gap-2 text-sm text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
          <MapPin size={16} />
          <span>Osparade ändringar — klicka &quot;Spara geofence&quot; för att aktivera</span>
        </div>
      )}
    </div>
  );
}
